import axios from 'axios';
import { API_URL } from '../config';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable credentials to send cookies with requests
  // CRITICAL: This must be true for cookies to be sent
  withCredentials: true,
});

// Add request interceptor to debug cookie sending
api.interceptors.request.use(
  (config) => {
    // Log that we're sending credentials
    if (config.withCredentials) {
      console.log(`[API] Sending request to ${config.url} with credentials (cookies should be sent)`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're in the initial auth check to prevent redirect loops
// This is exported so it can be shared with index.tsx for synchronized error handling
export let isInitialAuthCheck = true;
// Track if we're in the Google OAuth callback flow
export let isGoogleCallbackFlow = false;
let redirectTimeout: NodeJS.Timeout | null = null;

// Allow external code to mark initial auth check as complete
export const markInitialAuthCheckComplete = () => {
  isInitialAuthCheck = false;
};

// Allow external code to check if initial auth check is complete
export const getIsInitialAuthCheck = () => isInitialAuthCheck;

// Allow external code to set Google callback flow state
export const setGoogleCallbackFlow = (value: boolean) => {
  isGoogleCallbackFlow = value;
};

// Allow external code to check if in Google callback flow
export const getIsGoogleCallbackFlow = () => isGoogleCallbackFlow;

// Response interceptor for handling authentication errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle network errors (often CORS issues with withCredentials)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('Network Error - This is likely a CORS configuration issue.');
      console.error('Backend must have CORS configured with:');
      console.error('  - credentials: true');
      console.error('  - origin: specific URL (not "*")');
      console.error('  - allowedHeaders: ["Content-Type", "Authorization"]');
      console.error('See CORS_FIX_REQUIRED.md for details.');
      
      // Don't redirect on network errors - let the user see the error
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      // Cookie will be cleared by backend
      // Don't redirect during initial auth check or Google callback flow to prevent loops
      if (isInitialAuthCheck || isGoogleCallbackFlow) {
        // During initial auth check or Google callback, return a resolved promise with a special marker
        // instead of rejecting, to prevent React error overlay
        // This will be caught in AuthContext and handled gracefully
        return Promise.resolve({
          data: null,
          status: 401,
          statusText: 'Unauthorized',
          headers: error.response?.headers || {},
          config: error.config,
          isExpected401: true, // Marker to identify this as expected
        });
      }
      
      // Only redirect if not already on login page or auth callback
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath.includes('/login') || 
                        currentPath.includes('/auth/callback') || 
                        currentPath === '/' ||
                        currentPath === '';
      
      if (!isAuthPage) {
        // Clear any pending redirect timeout
        if (redirectTimeout) {
          clearTimeout(redirectTimeout);
        }
        
        // Use a small delay to prevent multiple redirects
        redirectTimeout = setTimeout(() => {
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
          redirectTimeout = null;
        }, 100);
      }
      
      // Reject the error - components should handle 401s gracefully
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default api;